import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
 

const services = [
  { icon: 'fa-solid fa-pen-ruler', title: '1. UI/UX Design', desc: 'Create attractive, intuitive, and user-friendly website interfaces that provide a smooth experience across desktop, tablet, and mobile devices.' },
  { icon: 'fa-solid fa-code', title: '2. Website Development', desc: 'Develop fast, scalable, and reliable websites using modern technologies and clean coding standards.' },
  { icon: 'fa-solid fa-mobile-screen-button', title: '3. Responsive Web Design', desc: 'Build websites that automatically adapt to different screen sizes, ensuring a consistent experience on all devices.' },
  { icon: 'fa-solid fa-gauge-high', title: '4. Performance Optimization', desc: 'Improve website loading speed, performance, and overall efficiency to provide visitors with a faster browsing experience.' },
  { icon: 'fa-solid fa-cart-shopping', title: '5. E-Commerce Development', desc: 'Create powerful online stores with product management, shopping carts, secure payments, and order management.' },
  { icon: 'fa-solid fa-magnifying-glass-chart', title: '6. SEO-Friendly Development', desc: 'Develop search-engine-friendly websites with optimized structure, metadata, URLs, and performance.' },
  { icon: 'fa-solid fa-shield-halved', title: '7. Website Security', desc: 'Implement essential security practices to protect websites, customer information, forms, and online transactions.' },
  { icon: 'fa-solid fa-screwdriver-wrench', title: '8. Website Maintenance', desc: 'Keep your website updated, secure, optimized, and running smoothly with regular backups and technical support.' },
  { icon: 'fa-solid fa-layer-group', title: '9. Custom Web Applications', desc: 'Develop customized web applications based on your business requirements, including portals and dashboards.' },
  { icon: 'fa-solid fa-plug', title: '10. API Integration', desc: 'Connect your website with third-party services, payment gateways, CRM systems, and business tools.' },
];

const whyChooseUs = [
  'Modern and high-performance websites',
  'Mobile-first and responsive design',
  'User-focused UI/UX',
  'Secure and scalable development',
  'SEO-friendly architecture',
  'Fast page loading',
  'E-commerce solutions',
  'Third-party API integrations',
  'Ongoing maintenance and support',
  'Designed for business growth',
];

const processSteps = [
  { step: '01', title: 'Discover', desc: 'Understand your business, audience, goals, and project requirements.' },
  { step: '02', title: 'Plan', desc: 'Create the website structure, features, technology stack, and development roadmap.' },
  { step: '03', title: 'Design', desc: 'Develop engaging UI/UX designs that match your brand identity.' },
  { step: '04', title: 'Develop', desc: 'Convert the designs into a functional, responsive, and scalable website.' },
  { step: '05', title: 'Test', desc: 'Test performance, responsiveness, security, functionality, and browser compatibility.' },
  { step: '06', title: 'Launch', desc: 'Deploy the website and make it available to your customers.' },
  { step: '07', title: 'Support', desc: 'Provide ongoing maintenance, updates, optimization, and technical support.' },
];

const techStack = [
  { category: 'Frontend', items: ['HTML5', 'CSS3', 'JavaScript', 'React.js', 'Bootstrap', 'Tailwind CSS'] },
  { category: 'Backend', items: ['Node.js', 'Express.js', 'PHP'] },
  { category: 'Database', items: ['MongoDB', 'MySQL'] },
  { category: 'Integrations', items: ['REST APIs', 'Payment Gateways', 'CRM', 'Social Media APIs'] },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const WebDesignDevelopment = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Web Innovations
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Web Design & <span className="gradient-text-cyan">Development</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we create modern, responsive, secure, and high-performance websites that help businesses build a commanding digital presence.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Start Your Project With Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Web Services by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE US & TECH STACK */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Choose <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Technologies We Master</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Development Workflow</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4 col-xl-3">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Build Your Digital Future with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Partner with Lidlbritain to engineer high-converting web applications and digital products custom-built for scaling your operations.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Get Started Today &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WebDesignDevelopment;